import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings, waNumber } from "@/context/SettingsContext";
import api from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1758995115659-06a6cb5787eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Accessories() {
  const { lang } = useLanguage();
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/accessories").then((r) => setItems(r.data.accessories)).finally(() => setLoading(false));
  }, []);

  const name = (a) => a[`name_${lang}`] || a.name_es;
  const desc = (a) => a[`description_${lang}`] || a.description_es;

  const waHref = (a) => {
    const msg = (lang === "es" ? "Hola 👋, me interesa el accesorio: " : "Hi 👋, I'm interested in the accessory: ") + name(a);
    return `https://wa.me/${waNumber(settings.phone)}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div data-testid="accessories-page">
      <section className="relative h-[52vh] min-h-[360px] overflow-hidden">
        <img src={HERO} alt="Accessories" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-5">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="overline text-gold mb-4">
            {lang === "es" ? "Complementos" : "Complements"}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="font-serif text-5xl sm:text-6xl font-light text-white">
            {lang === "es" ? "Accesorios" : "Accessories"}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="mt-4 text-white/80 max-w-xl">
            {lang === "es"
              ? "Estuches, organizadores y productos de cuidado para conservar tus joyas y relojes como el primer día."
              : "Cases, organizers and care products to keep your jewelry and watches like new."}
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center text-muted-foreground" data-testid="accessories-empty">
            {lang === "es" ? "Pronto añadiremos accesorios." : "Accessories coming soon."}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 lg:gap-8">
            {items.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                className="group flex flex-col"
                data-testid={`accessory-${item.id}`}
              >
                <div className="relative overflow-hidden aspect-[4/5] bg-secondary">
                  <img src={item.image} alt={name(item)} loading="lazy" className="hover-zoom-img h-full w-full object-cover" />
                </div>
                <h3 className="font-serif text-xl mt-4">{name(item)}</h3>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed flex-1">{desc(item)}</p>
                <p className="mt-3 text-gold font-medium">{formatPrice(item.price)}</p>
                <a href={waHref(item)} target="_blank" rel="noreferrer" data-testid={`accessory-inquire-${item.id}`}>
                  <Button variant="outline" className="rounded-none w-full h-11 mt-3 gap-2">
                    <MessageCircle className="h-4 w-4" /> {lang === "es" ? "Consultar" : "Inquire"}
                  </Button>
                </a>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
